/**
 * Work-order upload dropzone + live processing rail (Intake Desk Phase A).
 *
 * Uploads return 201 immediately (the server extracts in a background
 * worker), so each dropped file becomes a rail entry that advances
 * `uploading → extracting → lane result` by polling the intake item list
 * while anything is still processing. Failures are sticky rows with a
 * Retry button (manual POST /items/:id/process) plus a toast; items found
 * already `processing`/`failed` on page load render in the rail too, so
 * work orphaned by a server restart stays visible until it recovers.
 *
 * Second mode (CUSTOMER-EMAILS-AND-FILING.md §2c): "Filed test reports"
 * ingests official purveyor-portal test-report PDFs through
 * /api/filed-reports/upload (serial match → attach + submission submitted).
 * The default "Auto" mode tries the serial match first; PDFs that don't
 * match a pending filing flow to WO intake exactly as before.
 *
 * Shared by the Command Center intake card and the Intake Desk.
 */

import { useEffect, useRef, useState, type DragEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { normalizePdfFiles } from "@/lib/file-intake";
import { captureClientError } from "@/lib/sentry";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AlertTriangle, CheckCircle2, FileCheck2, Loader2, RefreshCw, UploadCloud, XCircle } from "lucide-react";

const ACCEPTED_WORK_ORDER = ".pdf,application/pdf,image/png,image/jpeg,image/webp";
const RAIL_POLL_MS = 2_500;

type DropMode = "auto" | "wo" | "filed";

const MODE_LABELS: Record<DropMode, string> = {
    auto: "Auto",
    wo: "Work orders",
    filed: "Filed test reports",
};

interface UploadEntry {
    /** Local key (file name is good enough within one drop batch). */
    key: string;
    name: string;
    itemId: string | null;
    state: "uploading" | "extracting" | "done" | "duplicate" | "error" | "filed" | "held";
    message?: string;
}

interface UploadResponse {
    itemId: string;
    status?: string;
    extractionAvailable?: boolean;
}

interface FiledMatchResult {
    submissionId?: string;
    jobId: string | null;
    jobDisplayNumber?: string | null;
    customerName?: string | null;
    serial: string;
    label: string;
}

interface FiledUploadFileResult {
    fileName: string;
    routed: "filed" | "held" | "intake";
    docClass?: string;
    matches: FiledMatchResult[];
    /** Matches made via the all-devices serial fallback. */
    assetMatches?: FiledMatchResult[];
    holdingId?: string;
    releasedJobIds?: string[];
    /** Multi-report PDF that was split per page (Issue #216). */
    multipage?: boolean;
    reportCount?: number;
    counts?: { filed: number; duplicate: number; review: number };
    summary?: string;
}

interface RailItem {
    id: string;
    status: string;
    file_name: string;
    updated_at: string;
}

const TERMINAL_MESSAGES: Record<string, string> = {
    auto_created: "Job created",
    ready_to_confirm: "Ready to confirm",
    ready_to_create: "Ready to create",
    needs_review: "Needs review",
    duplicate: "Duplicate work order",
    created: "Job created",
};

function terminalMessage(status: string): string {
    return TERMINAL_MESSAGES[status] ?? status.replace(/_/g, " ");
}

export function WorkOrderIntakeDropzone({ onChanged }: { onChanged?: () => void }) {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [mode, setMode] = useState<DropMode>("auto");
    const [entries, setEntries] = useState<UploadEntry[]>([]);
    const [uploading, setUploading] = useState(false);
    const [retrying, setRetrying] = useState<Set<string>>(new Set());
    // Failed items the office dismissed or retried-to-success this session.
    const notifiedFailures = useRef<Set<string>>(new Set());

    const anyLocalActive = entries.some((e) => e.state === "uploading" || e.state === "extracting");

    // One list poll drives everything: entry status transitions AND the
    // load-time recovery view (items already processing/failed when the page
    // opened). Poll only while something is actually in flight.
    const railQ = useQuery({
        queryKey: ["work-order-intake", "rail"],
        queryFn: () => api.get<{ items: RailItem[] }>("/api/work-order-intake/items?limit=50"),
        refetchInterval: (query) => {
            const items = query.state.data?.items ?? [];
            const serverActive = items.some((i) => i.status === "processing");
            return anyLocalActive || serverActive || retrying.size > 0 ? RAIL_POLL_MS : false;
        },
    });
    const railItems = railQ.data?.items ?? [];

    // Advance tracked entries off the freshest poll data (extracting → lane
    // result / sticky failure). Side effects (toast, Sentry, queue refresh)
    // fire exactly once per transition.
    useEffect(() => {
        const byId = new Map(railItems.map((i) => [i.id, i]));
        let changed = false;
        let reachedLane = false;
        const next = entries.map((e): UploadEntry => {
            if (!e.itemId || e.state !== "extracting") return e;
            const item = byId.get(e.itemId);
            if (!item || item.status === "processing") return e;
            changed = true;
            if (item.status === "failed") {
                if (!notifiedFailures.current.has(e.itemId)) {
                    notifiedFailures.current.add(e.itemId);
                    toast.error(`Extraction failed: ${e.name}`, {
                        description: "The file is saved — retry from the upload rail.",
                        duration: Infinity,
                        closeButton: true,
                    });
                    captureClientError(new Error("work-order extraction failed"), {
                        source: "WorkOrderIntakeDropzone",
                        itemId: e.itemId,
                        fileName: e.name,
                    });
                }
                return { ...e, state: "error", message: "Extraction failed" };
            }
            reachedLane = true;
            return { ...e, state: "done", message: terminalMessage(item.status) };
        });
        if (changed) {
            setEntries(next);
            onChanged?.();
            if (reachedLane) void qc.invalidateQueries({ queryKey: ["work-order-intake"] });
        }
    }, [railItems]);

    // Recovery rows: items processing/failed on the server that this session
    // didn't upload (page reload, another tab, or a server crash mid-run).
    const trackedIds = new Set(entries.map((e) => e.itemId).filter(Boolean));
    const recoveryRows = railItems.filter(
        (i) => !trackedIds.has(i.id) && (i.status === "processing" || i.status === "failed"),
    );

    async function uploadOne(file: File): Promise<FiledMatchResult[] | null> {
        const fd = new FormData();
        fd.append("file", file);
        const setEntry = (patch: Partial<UploadEntry>) =>
            setEntries((prev) => prev.map((e) => (e.key === file.name ? { ...e, ...patch } : e)));
        try {
            const res = await api.postForm<
                UploadResponse & {
                    diverted?: string;
                    routed?: string;
                    matches?: FiledMatchResult[];
                    assetMatches?: FiledMatchResult[];
                    multipage?: boolean;
                    counts?: { filed: number; duplicate: number; review: number };
                    summary?: string;
                }
            >("/api/work-order-intake/upload", fd);
            // Server-side document-class gate: a test report never becomes a
            // WO intake item, even in explicit "Work orders" mode.
            if (res.diverted === "test_report") {
                // A fat multi-report PDF was split per page (#220) — show the tally.
                if (res.multipage) {
                    const c = res.counts ?? { filed: 0, duplicate: 0, review: 0 };
                    setEntry({
                        state: c.review > 0 ? "held" : c.filed > 0 ? "filed" : "duplicate",
                        message: res.summary ?? "",
                    });
                    return [];
                }
                const matched = [...(res.matches ?? []), ...(res.assetMatches ?? [])];
                if (res.routed === "filed") {
                    setEntry({ state: "filed", message: matched.map((m) => m.label).join(" · ") || "Filed" });
                    return matched;
                }
                setEntry({ state: "held", message: "Test report — needs one answer (City Filing Queue)" });
                return [];
            }
            if (res.extractionAvailable === false) {
                setEntry({ itemId: res.itemId, state: "done", message: "Added — extraction off" });
            } else {
                setEntry({ itemId: res.itemId, state: "extracting", message: "Extracting…" });
            }
            return null;
        } catch (e) {
            if (e instanceof ApiError && e.status === 409) {
                const body = e.body as { itemId?: string };
                setEntry({ state: "duplicate", message: "Already uploaded" });
                if (body.itemId) {
                    toast.message(`${file.name} was already uploaded.`, {
                        action: { label: "Open Intake Desk", onClick: () => navigate("/jobs/work-orders") },
                    });
                }
            } else {
                // A silently-swallowed failure here means a vendor WO never
                // enters the queue — surface it loudly and report it.
                const serverMessage =
                    e instanceof ApiError
                        ? ((e.body as { error?: string; message?: string } | null)?.message ??
                          (e.body as { error?: string } | null)?.error ??
                          `HTTP ${e.status}`)
                        : e instanceof Error
                          ? e.message
                          : "Unknown error";
                setEntry({ state: "error", message: "Upload failed" });
                toast.error(`Upload failed: ${file.name}`, {
                    description: serverMessage,
                    duration: Infinity,
                    closeButton: true,
                });
                captureClientError(e, {
                    source: "WorkOrderIntakeDropzone.uploadOne",
                    fileName: file.name,
                    serverMessage,
                });
            }
        }
        return null;
    }

    /**
     * Try the filed-report serial match for one PDF. Returns the routing
     * decision (+ matches for the batch summary); "intake" means the caller
     * should continue down the regular work-order intake path (auto mode
     * only — and the server never says "intake" for a classified test report).
     */
    async function uploadFiledOne(
        file: File,
        auto: boolean,
    ): Promise<{
        routed: "filed" | "held" | "intake";
        matches: FiledMatchResult[];
        multipage?: boolean;
        summary?: string;
    }> {
        const setEntry = (patch: Partial<UploadEntry>) =>
            setEntries((prev) => prev.map((e) => (e.key === file.name ? { ...e, ...patch } : e)));
        const fd = new FormData();
        fd.append("files", file);
        fd.append("mode", auto ? "auto" : "filed");
        try {
            const res = await api.postForm<{ results: FiledUploadFileResult[] }>("/api/filed-reports/upload", fd);
            const r = res.results[0];
            if (!r) throw new Error("empty response");
            const matched = [...(r.matches ?? []), ...(r.assetMatches ?? [])];
            // Multi-report PDF (Issue #216): the server split it per page and
            // filed each; render the "28 filed · 3 duplicates · 8 need review"
            // summary rather than a single match label.
            if (r.multipage) {
                const c = r.counts ?? { filed: 0, duplicate: 0, review: 0 };
                const state: UploadEntry["state"] = c.review > 0 ? "held" : c.filed > 0 ? "filed" : "duplicate";
                setEntry({ state, message: r.summary ?? "" });
                if (r.releasedJobIds && r.releasedJobIds.length > 0) {
                    toast.success(
                        `Customer report email released for ${r.releasedJobIds.length} job${r.releasedJobIds.length === 1 ? "" : "s"} — all devices filed.`,
                    );
                }
                void qc.invalidateQueries({ queryKey: ["submissions"] });
                return { routed: c.review > 0 ? "held" : "filed", matches: [], multipage: true, summary: r.summary };
            }
            if (r.routed === "filed") {
                setEntry({ state: "filed", message: matched.map((m) => m.label).join(" · ") });
                if (r.releasedJobIds && r.releasedJobIds.length > 0) {
                    toast.success(
                        `Customer report email released for ${r.releasedJobIds.length} job${r.releasedJobIds.length === 1 ? "" : "s"} — all devices filed.`,
                    );
                }
                void qc.invalidateQueries({ queryKey: ["submissions"] });
                return { routed: "filed", matches: matched };
            }
            if (r.routed === "held") {
                setEntry({
                    state: "held",
                    message:
                        r.docClass === "test_report"
                            ? "Test report — needs one answer (City Filing Queue)"
                            : "No serial match — sent to holding list",
                });
                return { routed: "held", matches: [] };
            }
            return { routed: "intake", matches: [] };
        } catch (e) {
            if (auto) {
                // Auto mode degrades to the WO path rather than blocking intake.
                captureClientError(e, { source: "WorkOrderIntakeDropzone.uploadFiledOne", fileName: file.name });
                return { routed: "intake", matches: [] };
            }
            setEntry({ state: "error", message: "Upload failed" });
            toast.error(`Upload failed: ${file.name}`, { closeButton: true });
            captureClientError(e, { source: "WorkOrderIntakeDropzone.uploadFiledOne", fileName: file.name });
            return { routed: "held", matches: [] };
        }
    }

    interface RouteOutcome {
        filedMatches: FiledMatchResult[];
        held: boolean;
        multipage?: boolean;
        summary?: string;
    }

    async function routeOne(file: File): Promise<RouteOutcome> {
        const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
        if (mode === "filed") {
            const r = await uploadFiledOne(file, false);
            return { filedMatches: r.matches, held: r.routed === "held", multipage: r.multipage, summary: r.summary };
        }
        if (mode === "auto" && isPdf) {
            const r = await uploadFiledOne(file, true);
            if (r.routed !== "intake")
                return {
                    filedMatches: r.matches,
                    held: r.routed === "held",
                    multipage: r.multipage,
                    summary: r.summary,
                };
        }
        const diverted = await uploadOne(file);
        if (diverted !== null) return { filedMatches: diverted, held: diverted.length === 0 };
        return { filedMatches: [], held: false };
    }

    /** "17 matched to Koelsch #072653479, 0 unmatched." — one line per batch. */
    function batchSummary(outcomes: RouteOutcome[]): string | null {
        const filedFiles = outcomes.filter((o) => o.filedMatches.length > 0);
        const heldFiles = outcomes.filter((o) => o.held);
        if (filedFiles.length === 0 && heldFiles.length === 0) return null;
        if (filedFiles.length + heldFiles.length < 2 && heldFiles.length === 0) return null;
        const jobLabels = new Map<string, number>();
        for (const o of filedFiles) {
            for (const m of o.filedMatches) {
                const label =
                    [m.customerName, m.jobDisplayNumber ? `#${m.jobDisplayNumber}` : null].filter(Boolean).join(" ") ||
                    "matched jobs";
                jobLabels.set(label, (jobLabels.get(label) ?? 0) + 1);
                break; // count each FILE once, against its first match
            }
        }
        const parts = [...jobLabels.entries()].map(([label, n]) => `${n} matched to ${label}`);
        if (parts.length === 0) parts.push("0 matched");
        return `${parts.join(", ")}, ${heldFiles.length} unmatched.`;
    }

    async function handleFiles(fileList: FileList | File[]): Promise<void> {
        // A PDF saved without ".pdf" is still a PDF — fix its name/type before
        // the name-based filters below throw it away.
        const all = await normalizePdfFiles(Array.from(fileList));
        let files = all.filter(
            (f) => f.type === "application/pdf" || f.type.startsWith("image/") || f.name.toLowerCase().endsWith(".pdf"),
        );
        // LABEL THE DROP ZONE: this is the paperwork intake, not a photo
        // inbox (repro: IMG_3876 stuck in intake since 7/13). Images are
        // rejected with a friendly redirect in Auto + Filed modes; the
        // explicit "Work orders" tab still accepts scanned-image WOs.
        if (mode !== "wo") {
            const images = files.filter((f) => f.type.startsWith("image/") && !f.name.toLowerCase().endsWith(".pdf"));
            if (images.length > 0) {
                toast.message(
                    images.length === 1
                        ? `${images[0].name} looks like a photo — open the job and add it there.`
                        : `${images.length} files look like photos — open the job and add them there.`,
                    { description: "This drop zone is for WORK ORDER and TEST REPORT PDFs." },
                );
                files = files.filter((f) => !images.includes(f));
            }
        }
        if (mode === "filed") {
            const nonPdf = files.filter(
                (f) => !(f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")),
            );
            if (nonPdf.length > 0) toast.error("Filed test reports must be PDFs.");
            files = files.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
        }
        if (files.length === 0) {
            if (all.length > 0 && mode !== "wo") return; // image-only drop: the redirect toast said it all
            toast.error(
                mode === "filed"
                    ? "Drop the portal's PDF test reports."
                    : "Only PDF or image work orders are supported.",
            );
            return;
        }
        setEntries((prev) => [
            ...prev.filter((e) => !files.some((f) => f.name === e.key)),
            ...files.map((f) => ({ key: f.name, name: f.name, itemId: null, state: "uploading" as const })),
        ]);
        setUploading(true);
        // Uploads are instant now (no inline extraction) — fire them all.
        const outcomes = await Promise.all(files.map((f) => routeOne(f)));
        setUploading(false);
        // A split multi-report PDF carries its own rich tally ("32 reports —
        // 28 filed · 3 duplicates · 8 need review") — surface each directly.
        for (const o of outcomes) if (o.multipage && o.summary) toast.success(o.summary, { duration: 12_000 });
        // Batch drops of ordinary single reports process as a set with ONE line.
        const summary = batchSummary(outcomes.filter((o) => !o.multipage));
        if (summary) toast.success(summary, { duration: 10_000 });
        onChanged?.();
        void qc.invalidateQueries({ queryKey: ["work-order-intake", "rail"] });
    }

    async function retryItem(itemId: string, name: string): Promise<void> {
        setRetrying((prev) => new Set(prev).add(itemId));
        setEntries((prev) =>
            prev.map((e) => (e.itemId === itemId ? { ...e, state: "extracting", message: "Retrying…" } : e)),
        );
        notifiedFailures.current.delete(itemId);
        try {
            const res = await api.post<{ result?: { status?: string; error?: string } }>(
                `/api/work-order-intake/items/${itemId}/process`,
                {},
            );
            if (res.result?.status === "failed") {
                toast.error(`Extraction failed again: ${name}`, {
                    description: res.result.error ?? undefined,
                    closeButton: true,
                });
            }
        } catch (e) {
            toast.error(`Retry failed: ${name}`);
            captureClientError(e, { source: "WorkOrderIntakeDropzone.retry", itemId });
        } finally {
            setRetrying((prev) => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
            });
            onChanged?.();
            void qc.invalidateQueries({ queryKey: ["work-order-intake", "rail"] });
        }
    }

    const onDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files?.length) void handleFiles(e.dataTransfer.files);
    };

    function railRow(opts: {
        key: string;
        name: string;
        itemId: string | null;
        state: UploadEntry["state"];
        message?: string;
    }) {
        const { key, name, itemId, state, message } = opts;
        const failed = state === "error" && itemId != null;
        return (
            <li key={key} className="flex items-center gap-2 text-xs">
                {(state === "uploading" || state === "extracting") && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-foreground/60" />
                )}
                {state === "done" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                {state === "filed" && <FileCheck2 className="h-3.5 w-3.5 text-emerald-600" />}
                {(state === "duplicate" || state === "held") && (
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                )}
                {state === "error" && <XCircle className="h-3.5 w-3.5 text-destructive" />}
                <span className="min-w-0 flex-1 truncate">{name}</span>
                {message && (
                    <span className={cn("shrink-0", state === "error" ? "text-destructive" : "text-foreground/60")}>
                        {message}
                    </span>
                )}
                {failed && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-6 shrink-0 gap-1 px-2"
                        disabled={retrying.has(itemId)}
                        onClick={() => void retryItem(itemId, name)}
                    >
                        {retrying.has(itemId) ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3 w-3" />
                        )}
                        Retry
                    </Button>
                )}
            </li>
        );
    }

    return (
        <div className="mb-3">
            <div className="mb-1.5 flex items-center gap-1" role="tablist" aria-label="Drop zone mode">
                {(Object.keys(MODE_LABELS) as DropMode[]).map((m) => (
                    <button
                        key={m}
                        type="button"
                        role="tab"
                        aria-selected={mode === m}
                        onClick={() => setMode(m)}
                        className={cn(
                            "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                            mode === m
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-card text-muted-foreground hover:border-primary/50",
                        )}
                    >
                        {MODE_LABELS[m]}
                    </button>
                ))}
            </div>
            <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={cn(
                    "flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-card px-4 py-5 text-center text-card-foreground shadow-sm transition-colors",
                    dragOver
                        ? "border-primary bg-secondary"
                        : "border-border hover:border-primary/50 hover:bg-secondary",
                )}
            >
                {uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                ) : (
                    <UploadCloud className="h-5 w-5 text-primary" />
                )}
                <p className="text-sm font-medium">
                    {mode === "filed"
                        ? "Drop filed test reports here to match & file"
                        : mode === "wo"
                          ? "Drop work orders here to upload"
                          : "Drop WORK ORDERS and TEST REPORTS (PDF) here"}
                </p>
                <p className="text-[11px] text-muted-foreground">
                    {mode === "filed"
                        ? "One report or a whole multi-page PDF — each page is matched by serial and filed to its device"
                        : mode === "wo"
                          ? "PDF or image — extraction runs in the background, keep working"
                          : "Photos and other images belong on the job or device, not here"}
                </p>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={mode === "filed" ? ".pdf,application/pdf" : ACCEPTED_WORK_ORDER}
                    className="hidden"
                    onChange={(e) => {
                        if (e.target.files?.length) void handleFiles(e.target.files);
                        e.target.value = "";
                    }}
                />
            </div>
            {(entries.length > 0 || recoveryRows.length > 0) && (
                // Solid card surface matching the lane cards — rail rows sit on
                // the dark shell background otherwise and become unreadable.
                <ul className="mt-2 space-y-1 rounded-xl border border-border/70 bg-card px-3 py-2.5 text-card-foreground shadow-(--shadow-card)">
                    {entries.map((entry) =>
                        railRow({
                            key: `upload:${entry.key}`,
                            name: entry.name,
                            itemId: entry.itemId,
                            state: entry.state,
                            message: entry.message,
                        }),
                    )}
                    {recoveryRows.map((item) =>
                        railRow({
                            key: `recovered:${item.id}`,
                            name: item.file_name,
                            itemId: item.id,
                            state: item.status === "failed" ? "error" : "extracting",
                            message: item.status === "failed" ? "Extraction failed" : "Extracting…",
                        }),
                    )}
                </ul>
            )}
        </div>
    );
}
